import { FormikProps } from "formik"
import { isNil } from "lodash"
import { enqueueSnackbar } from "notistack"
import { useRef } from "react"
import { useNavigate } from "react-router-dom"

import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { TENANT_ADMIN_ROUTE, TENANT_USERS_ROUTE, getTenantUsersRoute } from "../../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    BreadcrumbLink,
    Breadcrumb,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { selectEffectiveCompanyId, selectEffectiveUserGroup } from "../../../../../context/contextSelectors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { UserType } from "../../../../../login/loginTypes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { AdministratorForm } from "../../../../../users/administrators/components/AdministratorForm"
import { AdministratorFormValues } from "../../../../../users/administrators/administratorFormTypes"
import { toCreateAdministratorFields } from "../../../../../users/administrators/utils/administratorBody"
import { createAdmin } from "../../../../../users/userApi"
import { CreateTenantAdmin as CreateTenantAdminBody } from "../../../../../users/userTypes"
import { TenantAdminQuery } from "../../../tenantAdminApi"

export function CreateTenantAdminPage() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectEffectiveUserGroup)

    const formRef = useRef<FormikProps<AdministratorFormValues>>(null)

    const { mutateAsync: callCreateAdmin, isLoading: isCreating } = useApiMutation(createAdmin, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [TenantAdminQuery.ADMINS_FOR_TENANT] })

            navigate(getTenantUsersRoute("admin"))
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    async function handleSubmit(values: AdministratorFormValues) {
        if (isNil(companyId) || isNil(userGroupId)) {
            enqueueSnackbar(l10n.getString("office-manager-create-admin-missing-context-error"), { variant: "error" })
            return
        }

        const admin: CreateTenantAdminBody = {
            type: UserType.OFFICE_MANAGER,
            ...toCreateAdministratorFields(values),
            officeManagerDetails: { companyId, userGroupId },
        }

        await callCreateAdmin({ admin })
    }

    return (
        <>
            <LocalizedStrict id="administrators-create-page-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.TENANT_CREATE_OFFICE_MANAGER}
                    title="Create administrator"
                    rightElement={
                        <LocalizedStrict id="administrators-create-page-save-button">
                            <StandardLoadingButton type="submit" onClick={handleSaveClick} loading={isCreating}>
                                Create
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="tenant-nav-item-home">
                            <BreadcrumbLink to={TENANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-users">
                            <BreadcrumbLink to={TENANT_USERS_ROUTE}>Users</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="administrators-create-breadcrumb">
                            <Breadcrumb>Create administrator</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <AdministratorForm
                ref={formRef}
                adminType={UserType.OFFICE_MANAGER}
                isEditMode={false}
                onSubmit={handleSubmit}
            />
        </>
    )
}
